import { useEffect, useState } from 'react';

import { getLocationDDL } from '../../../common/utils/dropDown';

export interface SavedLocation {
  id: string;
  name: string;
  address: string;
}

const CACHE_MS = 60_000;
/** An empty answer is kept briefly: it may be a failed load (the loader answers [] then), not a company with none. */
const EMPTY_CACHE_MS = 5_000;
let cached: {
  token: string;
  at: number;
  ttl: number;
  load: Promise<SavedLocation[]>;
} | null = null;

/** Loads once per minute and token, however many boxes (origin, destination, remounts) ask. */
const loadSavedLocations = (token: string): Promise<SavedLocation[]> => {
  if (cached && cached.token === token && Date.now() - cached.at < cached.ttl) {
    return cached.load;
  }
  // getLocationDDL answers [] on a failure, so the box simply has no saved rows.
  const load = getLocationDDL(token, true).then((rows) =>
    rows
      .filter((row) => row.id && (row.name || row.address))
      .map((row) => ({
        id: String(row.id),
        name: row.name ?? '',
        address: row.address ?? '',
      }))
  );
  const entry = { token, at: Date.now(), ttl: CACHE_MS, load };
  cached = entry;
  const _shorten = load.then((rows) => {
    if (rows.length === 0) entry.ttl = EMPTY_CACHE_MS;
  });
  return load;
};

/** For tests: forget what was loaded. */
export const clearSavedLocationsCache = (): void => {
  cached = null;
};

/** The company's saved locations (Settings > Locations) for the origin/destination boxes. */
export const useSavedLocations = (token: string): SavedLocation[] => {
  const [locations, setLocations] = useState<SavedLocation[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLocations([]);
    const _ = loadSavedLocations(token).then((rows) => {
      if (!cancelled) setLocations(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return locations;
};

/** Saved locations whose name or address contains the text, best (name) matches first. */
export const matchSavedLocations = (
  locations: SavedLocation[],
  text: string,
  limit = 5
): SavedLocation[] => {
  const query = text.trim().toLowerCase();
  if (!query) return [];
  const byName = locations.filter((l) => l.name.toLowerCase().includes(query));
  const byAddress = locations.filter(
    (l) =>
      !l.name.toLowerCase().includes(query) &&
      l.address.toLowerCase().includes(query)
  );
  return [...byName, ...byAddress].slice(0, limit);
};
